// ============================================================================
// ecgStudies.ts — чтение и запись исследований ЭКГ (справочник cardio_studies).
//
// Возможности экрана врача:
//   • список доступных исследований (SSoT прав — сервер: права объекта +
//     OwnScope; клиент НЕ расширяет выдачу);
//   • карточка исследования (снимок, измерения, находки) и ЗАКЛЮЧЕНИЕ врача —
//     записывается в реквизит comment той же записи существующим RPC
//     UpdateTableRow (отдельного серверного контракта не вводим);
//   • пациент — владелец исследования (owner_user_id, представление с сервера)
//     либо, если в базе есть явный реквизит пациента (patient_id и т.п.),
//     значение этого реквизита (см. lib/ecgPatients).
//
// Загрузка фото — FileService.UploadFile (ownerId НЕ задаётся: файл — часть
// разбора, привязка через file_id). Строки ТЧ «Находки» — createTableRow с
// parent_id = id разбора; физические колонки ТЧ: «Код», «Вероятность».
// SSoT имён полей — hydra.medic.ecg / buildkit/cardio.go.
// ============================================================================

import type { Field, TableDataRow, TabularSection } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { abortUpload, uploadFile } from '@/lib/chunkedUpload';
import { isTransportError } from '@/lib/errors';

export const CARDIO_STUDIES_TABLE = 'cardio_studies';
export const CARDIO_FINDINGS_TS_NAME = 'Находки';
export const CARDIO_FINDINGS_CODE_COLUMN = 'Код';
export const CARDIO_FINDINGS_PROBABILITY_COLUMN = 'Вероятность';
export const CARDIO_OWNER_COLUMN = 'owner_user_id';
/** Реквизит заключения врача: единственный свободный текст разбора. */
export const CARDIO_CONCLUSION_COLUMN = 'comment';

/**
 * Возможные колонки пациента в записи исследования. Поставка 1.0.2 реквизита
 * пациента не содержит — тогда пациентом считается владелец записи. Если в
 * базе добавят явную ссылку на картотеку, экран подхватит её без правок кода.
 */
const PATIENT_COLUMN_CANDIDATES = ['patient_id', 'patient', 'patient_ref', 'patient_user_id'];

export const ECG_STATUS_LABELS: Record<string, string> = {
    processed: 'Обработан',
    retake: 'Нужно переснять',
    error: 'Ошибка',
};

export const ECG_SOURCE_LABELS: Record<string, string> = {
    photo: 'Фото',
    device: 'Файл аппарата',
};

export type EcgQualityVerdict = 'good' | 'acceptable' | 'warning' | 'retake' | 'bad' | 'unknown';

export const ECG_QUALITY_LABELS: Record<EcgQualityVerdict, string> = {
    good: 'Хорошее',
    acceptable: 'Приемлемое',
    warning: 'С оговорками',
    retake: 'Требуется повтор',
    bad: 'Плохое',
    unknown: 'Не указано',
};

const QUALITY_VERDICTS: EcgQualityVerdict[] = ['good', 'acceptable', 'warning', 'retake', 'bad'];

/** Вердикт качества из строки «good · резкость 0.98 · 12 отведений». */
export function ecgQualityVerdict(qualityLine: string): EcgQualityVerdict {
    const head = String(qualityLine || '').split('·')[0].trim().toLowerCase();
    for (const verdict of QUALITY_VERDICTS) {
        if (head === verdict || head.startsWith(`${verdict} `)) return verdict;
    }
    return 'unknown';
}

export interface EcgStudyMeasurements {
    hrBpm: number | null;
    prMs: number | null;
    qrsMs: number | null;
    qtMs: number | null;
    qtcMs: number | null;
    axisDeg: number | null;
}

export interface EcgStudyFinding {
    code: string;
    probability: number | null;
}

export interface SaveEcgStudyInput {
    ownerId: string;
    file: File;
    status: 'processed' | 'retake';
    qualityLine: string;
    reason: string;
    pipelineVersion: string;
    measurements: EcgStudyMeasurements;
    findings: EcgStudyFinding[];
    /** Имя разбора (для очереди — момент разбора, а не момент отправки). */
    studyName?: string;
}

export interface EcgFindingFailure {
    code: string;
    error: unknown;
}

export interface SaveEcgStudyResult {
    studyId: string;
    fileId: string;
    findingsSaved: number;
    findingsFailed: EcgFindingFailure[];
    /** Разбор отложен в офлайн-очередь (studyId ещё нет). */
    queued?: boolean;
    /** Идентификатор элемента очереди. */
    queueId?: string;
}

/**
 * EcgSaveOfflineError — связь с сервером недоступна: разбор нужно положить
 * в офлайн-очередь (это НЕ ошибка данных, повтор детерминирован при связи).
 */
export class EcgSaveOfflineError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'EcgSaveOfflineError';
    }
}

interface RowValue {
    column: string;
    value: string;
}

export interface EcgStudyListItem {
    id: string;
    title: string;
    createdAt: string;
    statusCode: string;
    statusLabel: string;
    sourceCode: string;
    sourceLabel: string;
    quality: string;
    qualityVerdict: EcgQualityVerdict;
    measurements: EcgStudyMeasurements;
    /** Заключение врача (реквизит «Комментарий» записи). */
    conclusion: string;
    pipelineVersion: string;
    fileId: string;
    /** Владелец записи (представление с сервера; UUID не показывается). */
    ownerId: string;
    ownerName: string;
    /** Явный реквизит пациента (если он есть в базе) — иначе пусто. */
    patientId: string;
    patientName: string;
}

/** Сохранённый разбор вместе со строками ТЧ «Находки». */
export interface EcgStudyDetail extends EcgStudyListItem {
    findings: EcgStudyFinding[];
}

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

/** Имя разбора: «Разбор ДД.ММ.ГГГГ ЧЧ:ММ» (локальное время пользователя). */
export function formatStudyName(date: Date = new Date()): string {
    const day = pad2(date.getDate());
    const month = pad2(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad2(date.getHours());
    const minutes = pad2(date.getMinutes());
    return `Разбор ${day}.${month}.${year} ${hours}:${minutes}`;
}

/** Строка качества: «good · резкость 0.98 · 12 отведений» (пропуски допустимы). */
export function buildQualityLine(verdict: string, sharpnessScore: number | null, leads: number | null): string {
    const parts: string[] = [];
    if (verdict) parts.push(verdict);
    if (sharpnessScore !== null) parts.push(`резкость ${sharpnessScore.toFixed(2)}`);
    if (leads !== null) parts.push(`${leads} отведений`);
    return parts.join(' · ') || '—';
}

let findingsTableCache = '';

/** Физическая таблица ТЧ «Находки»: {каталог}_{имяТЧ в нижнем регистре}. */
async function resolveFindingsTable(): Promise<string> {
    if (findingsTableCache) return findingsTableCache;
    const response = await getSDK().metadata.listTabularSections({
        entityType: 'catalog',
        entityCode: CARDIO_STUDIES_TABLE,
    });
    const sections: TabularSection[] = response.items || [];
    const section = sections.find((item) => item.name === CARDIO_FINDINGS_TS_NAME || item.synonym === CARDIO_FINDINGS_TS_NAME);
    if (!section) {
        throw new Error(`В базе не найдена табличная часть «${CARDIO_FINDINGS_TS_NAME}» справочника разборов`);
    }
    const suffix = (section.code || section.name).toLowerCase();
    if (!suffix) throw new Error('У табличной части «Находки» нет имени');
    findingsTableCache = `${CARDIO_STUDIES_TABLE}_${suffix}`;
    return findingsTableCache;
}

function buildStudyValues(input: SaveEcgStudyInput, fileId: string): RowValue[] {
    const values: RowValue[] = [
        { column: 'name', value: input.studyName || formatStudyName() },
        { column: 'owner_user_id', value: input.ownerId },
        { column: 'source', value: 'photo' },
        { column: 'file_id', value: fileId },
        { column: 'status', value: input.status },
        { column: 'quality', value: input.qualityLine },
    ];

    const numeric: Array<[string, number | null]> = [
        ['hr', input.measurements.hrBpm],
        ['pr', input.measurements.prMs],
        ['qrs', input.measurements.qrsMs],
        ['qt', input.measurements.qtMs],
        ['qtc', input.measurements.qtcMs],
        ['axis', input.measurements.axisDeg],
    ];
    for (const [column, value] of numeric) {
        if (value !== null) values.push({ column, value: String(value) });
    }
    if (input.pipelineVersion) {
        values.push({ column: 'pipeline_version', value: input.pipelineVersion });
    }
    if (input.status === 'retake' && input.reason) {
        values.push({ column: 'comment', value: input.reason });
    }
    return values;
}

export interface PersistEcgStudyOptions {
    /** Файл уже загружен (повтор после сбоя создания записи) — не грузим заново. */
    fileId?: string;
    /** Колбэк после успешной загрузки файла (для офлайн-очереди). */
    onFileUploaded?: (fileId: string) => void;
    /** Прогресс передачи снимка 0..1 (показывается в офлайн-очереди). */
    onFileProgress?: (fraction: number) => void;
}

/**
 * persistEcgStudy — загрузить фото и сохранить разбор со строками ТЧ «Находки».
 * Онлайн-часть: офлайн-политику (очередь) применяет lib/ecgOfflineQueue.
 * Ошибка загрузки/создания записи — исключение; ошибки отдельных находок
 * возвращаются в findingsFailed (разбор уже сохранён).
 */
export async function persistEcgStudy(
    input: SaveEcgStudyInput,
    options: PersistEcgStudyOptions = {},
): Promise<SaveEcgStudyResult> {
    if (!input.ownerId) throw new Error('Не удалось определить текущего пользователя');
    const findingsTable = await resolveFindingsTable();
    const sdk = getSDK();

    let fileId = String(options.fileId || '');
    if (!fileId) {
        // Единый механизм передачи файлов (чанки/докачка), а не прямой unary:
        // на телефоне крупный снимок не упирается в лимит одного запроса.
        const job = await uploadFile(input.file, { onProgress: options.onFileProgress });
        if (job.status === 'queued') {
            // Файл попал в общую очередь загрузки, но разбор целиком отложит
            // собственная очередь ЭКГ — убираем дубль, чтобы снимок не ушёл дважды.
            await abortUpload(job.id);
            throw new EcgSaveOfflineError('Нет связи — снимок ЭКГ ожидает отправки');
        }
        if (job.status !== 'done' || !job.fileId) {
            const message = job.error || 'Не удалось загрузить снимок ЭКГ';
            if (isTransportError(new Error(message))) throw new EcgSaveOfflineError(message);
            throw new Error(message);
        }
        fileId = job.fileId;
        options.onFileUploaded?.(fileId);
    }

    const created = await sdk.metadata.createTableRow({
        schema: 'public',
        table: CARDIO_STUDIES_TABLE,
        values: buildStudyValues(input, fileId),
    });
    const studyId = String(created.id || '');
    if (!studyId) throw new Error('Сервер не вернул идентификатор сохранённого разбора');

    const findingsFailed: EcgFindingFailure[] = [];
    let findingsSaved = 0;
    for (const finding of input.findings) {
        const rowValues: RowValue[] = [
            { column: 'parent_id', value: studyId },
            { column: CARDIO_FINDINGS_CODE_COLUMN, value: finding.code },
        ];
        if (finding.probability !== null) {
            rowValues.push({ column: CARDIO_FINDINGS_PROBABILITY_COLUMN, value: String(finding.probability) });
        }
        try {
            await sdk.metadata.createTableRow({ schema: 'public', table: findingsTable, values: rowValues });
            findingsSaved++;
        } catch (error) {
            findingsFailed.push({ code: finding.code, error });
        }
    }

    return { studyId, fileId, findingsSaved, findingsFailed };
}

function cellsOf(row: TableDataRow): Map<string, string> {
    const cells = new Map<string, string>();
    for (const value of row.values || []) {
        if (value.value === '<nil>') continue;
        cells.set(value.column, value.value);
    }
    return cells;
}

function numberOrNull(raw: string): number | null {
    if (!raw) return null;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Реквизит пациента в базе (если он есть): ссылочная колонка с именем из
 * PATIENT_COLUMN_CANDIDATES. Отсутствие — нормальный режим (пациент = владелец),
 * поэтому пустая строка кэшируется; ошибка чтения метаданных не кэшируется и
 * пробрасывается — список не подменяется частичными данными молча.
 */
let patientColumnPromise: Promise<string> | null = null;

export async function resolveEcgPatientColumn(): Promise<string> {
    if (!patientColumnPromise) {
        patientColumnPromise = (async () => {
            const response = await getSDK().metadata.listFields({
                entityType: 'catalog',
                entityCode: CARDIO_STUDIES_TABLE,
            });
            const fields: Field[] = response.items || [];
            const match = fields.find((field) => {
                const column = String(field.code || field.name || '').toLowerCase();
                if (!PATIENT_COLUMN_CANDIDATES.includes(column)) return false;
                const dataType = String(field.dataType || '');
                return dataType === 'reference' || dataType === 'uuid';
            });
            return match ? String(match.code || match.name) : '';
        })().catch((error: unknown) => {
            // Сквозная ошибка — не кэшируем неудачу: следующий вызов повторит.
            patientColumnPromise = null;
            throw error;
        });
    }
    return patientColumnPromise;
}

function studyFromRow(row: TableDataRow, patientColumn: string): EcgStudyListItem {
    const cells = cellsOf(row);
    const statusCode = cells.get('status') || '';
    const sourceCode = cells.get('source') || '';
    const quality = cells.get('quality') || '';
    const hr = numberOrNull(cells.get('hr') || '');
    return {
        id: row.id,
        title: cells.get('name') || '',
        createdAt: cells.get('created_at') || '',
        statusCode,
        statusLabel: cells.get('status_presentation') || ECG_STATUS_LABELS[statusCode] || statusCode || '—',
        sourceCode,
        sourceLabel: cells.get('source_presentation') || ECG_SOURCE_LABELS[sourceCode] || sourceCode,
        quality,
        qualityVerdict: ecgQualityVerdict(quality),
        measurements: {
            hrBpm: hr,
            prMs: numberOrNull(cells.get('pr') || ''),
            qrsMs: numberOrNull(cells.get('qrs') || ''),
            qtMs: numberOrNull(cells.get('qt') || ''),
            qtcMs: numberOrNull(cells.get('qtc') || ''),
            axisDeg: numberOrNull(cells.get('axis') || ''),
        },
        conclusion: cells.get(CARDIO_CONCLUSION_COLUMN) || '',
        pipelineVersion: cells.get('pipeline_version') || '',
        fileId: cells.get('file_id') || '',
        ownerId: cells.get(CARDIO_OWNER_COLUMN) || '',
        ownerName: cells.get(`${CARDIO_OWNER_COLUMN}_presentation`) || '',
        patientId: patientColumn ? cells.get(patientColumn) || '' : '',
        patientName: patientColumn ? cells.get(`${patientColumn}_presentation`) || '' : '',
    };
}

/** Куда фильтровать исследования: владелец записи или явный пациент. */
export interface EcgStudyScope {
    ownerId?: string;
    patient?: { id: string; source: 'patient' | 'owner' };
}

export interface EcgStudyQuery extends EcgStudyScope {
    limit?: number;
}

/**
 * listEcgStudies — доступные пользователю исследования (страницами, до limit).
 * Без scope — ВСЯ доступная выдача (права объекта + OwnScope решает сервер):
 * это очередь врача. С ownerId/patient — история конкретного пациента.
 */
export async function listEcgStudies(query: EcgStudyQuery = {}): Promise<EcgStudyListItem[]> {
    const limit = query.limit && query.limit > 0 ? query.limit : 500;
    const patientColumn = await resolveEcgPatientColumn();

    const filters: Array<{ column: string; value: string }> = [];
    if (query.patient) {
        if (query.patient.source === 'patient') {
            if (!patientColumn) {
                throw new Error('В базе нет реквизита пациента — история по картотеке недоступна');
            }
            filters.push({ column: patientColumn, value: query.patient.id });
        } else {
            filters.push({ column: CARDIO_OWNER_COLUMN, value: query.patient.id });
        }
    } else if (query.ownerId) {
        filters.push({ column: CARDIO_OWNER_COLUMN, value: query.ownerId });
    }

    const pageSize = 200;
    const items: EcgStudyListItem[] = [];
    for (let page = 0; items.length < limit; page++) {
        const response = await getSDK().metadata.listTableData({
            schema: 'public',
            table: CARDIO_STUDIES_TABLE,
            pageSize: Math.min(pageSize, limit - items.length),
            page,
            orderBy: 'created_at',
            orderDesc: true,
            includeMarked: false,
            filters,
        });
        const rows = response.rows || [];
        items.push(...rows.map((row) => studyFromRow(row, patientColumn)));
        if (rows.length < pageSize) break;
    }
    return items;
}

/** listMyEcgStudies — исследования текущего пользователя (обратная совместимость). */
export async function listMyEcgStudies(ownerId: string, limit = 500): Promise<EcgStudyListItem[]> {
    if (!ownerId) return [];
    return listEcgStudies({ ownerId, limit });
}

/**
 * saveEcgStudyConclusion — записать заключение врача в карточку исследования
 * (реквизит «Комментарий»). Существующий RPC, без новых серверных контрактов;
 * права и блокировки проверяет сервер. Пустая строка очищает заключение.
 */
export async function saveEcgStudyConclusion(studyId: string, conclusion: string): Promise<void> {
    if (!studyId) throw new Error('Не указано исследование');
    await getSDK().metadata.updateTableRow({
        schema: 'public',
        table: CARDIO_STUDIES_TABLE,
        rowId: studyId,
        values: [{ column: CARDIO_CONCLUSION_COLUMN, value: conclusion.trim() }],
    });
}

/** Строки ТЧ «Находки» сохранённого разбора. */
export async function listEcgStudyFindings(studyId: string): Promise<EcgStudyFinding[]> {
    if (!studyId) return [];
    const table = await resolveFindingsTable();
    const response = await getSDK().metadata.listTableData({
        schema: 'public',
        table,
        pageSize: 200,
        page: 0,
        includeMarked: false,
        filters: [{ column: 'parent_id', value: studyId }],
    });
    return (response.rows || []).map((row) => {
        const cells = cellsOf(row);
        return {
            code: cells.get(CARDIO_FINDINGS_CODE_COLUMN) || '—',
            probability: numberOrNull(cells.get(CARDIO_FINDINGS_PROBABILITY_COLUMN) || ''),
        };
    });
}

/** loadEcgStudyDetail — сохранённый разбор по id + его находки. */
export async function loadEcgStudyDetail(studyId: string): Promise<EcgStudyDetail> {
    if (!studyId) throw new Error('Не указано исследование');
    const patientColumn = await resolveEcgPatientColumn();
    const response = await getSDK().metadata.listTableData({
        schema: 'public',
        table: CARDIO_STUDIES_TABLE,
        pageSize: 1,
        page: 0,
        includeMarked: false,
        filters: [{ column: 'id', value: studyId }],
    });
    const row = (response.rows || [])[0];
    if (!row) throw new Error('Исследование не найдено или недоступно');
    const study = studyFromRow(row, patientColumn);
    const findings = await listEcgStudyFindings(studyId);
    return { ...study, findings };
}
